'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings, ShoppingBag, User as UserIcon } from 'lucide-react';
import Link from 'next/link';

import { Avatar, IconWithBadge, Menu, MenuItem, Popover } from '@nutralab/ui';

import styles from './AccountNavIcon.module.scss';

import { LoginModal } from '@/app/_modals/LoginModal';
import { useAuth } from '@/app/_providers/AuthProvider';
import { useModal } from '@/app/_providers/ModalProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { ANALYTICS_GOALS, AppLayer, LkLinks } from '@/shared/constants';
import { reachGoal } from '@/shared/utils';

interface AccountNavIconProps {
    label: string;
    lkLink?: string;
}

/** The account item of the navbar: sign-in for a guest, a menu with orders, settings and sign-out for a user. */
export function AccountNavIcon({ label, lkLink }: AccountNavIconProps): React.ReactElement {
    const { t } = useTranslation();
    const { isAuthenticated, logout } = useAuth();
    const { openModal } = useModal();
    const { region, current_language } = useSiteConfig();

    // The personal account opens in the storefront's language (its language detector reads ?lng= first).
    const lkHref = (path: LkLinks): string => `${lkLink}${path}?lng=${encodeURIComponent(current_language.code)}`;

    if (!isAuthenticated) {
        return (
            <IconWithBadge
                icon={<UserIcon />}
                label={label}
                onClick={() => {
                    reachGoal(region.code, ANALYTICS_GOALS.CLICK_REGISTRATION);
                    openModal({ ModalComponent: LoginModal });
                }}
            />
        );
    }

    return (
        <Popover
            zIndex={AppLayer.POPOVER}
            trigger={
                <button type="button" className={styles.trigger} aria-label={label}>
                    <Avatar size="lg" />
                </button>
            }
        >
            <Menu>
                <MenuItem icon={<ShoppingBag />} asChild>
                    <Link href={lkHref(LkLinks.ORDERS)}>{t('navbar.account-menu.orders')}</Link>
                </MenuItem>
                <MenuItem icon={<Settings />} asChild>
                    <Link href={lkHref(LkLinks.SETTINGS)}>{t('navbar.account-menu.settings')}</Link>
                </MenuItem>
                <MenuItem icon={<LogOut />} color="error" onClick={() => void logout()}>
                    {t('navbar.account-menu.logout')}
                </MenuItem>
            </Menu>
        </Popover>
    );
}
